import React from 'react';
import {
    Drawer,
    Box,
    Button,
    Slider,
    Typography,
    IconButton,
    Divider,
    ButtonBase,
    Tooltip,
    Switch,
    Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import FontSizeSlider from './FontSizeSlider';
import ColorPicker from './ColorPicker';
import { useTheme } from '../theme/ThemeContext';
import { PAPER_MODES, ACCENT_COLORS } from '../theme/themePresets';
import {
    READER_FONT_FAMILIES,
    READER_LINE_HEIGHTS,
    useReaderTypography,
} from '../theme/ReaderTypographyContext';
import { getProgressStyle, setProgressStylePref } from './ChapterProgress';
import {
    getWakeLockPref,
    setWakeLockPref,
    isWakeLockSupported,
} from '../hooks/useWakeLock';
import {
    getPullNextChapterPref,
    setPullNextChapterPref,
} from './Reader/usePullNextChapter';

const SettingsPanel = ({ open, onClose }) => {
    const {
        paperMode,
        accentColor,
        contentWidth,
        setPaperMode,
        setAccentColor,
        setContentWidth,
    } = useTheme();
    const {
        fontFamilyId,
        lineHeight,
        setFontFamilyId,
        setLineHeight,
    } = useReaderTypography();

    const [progressStyle, setProgressStyleState] = React.useState(getProgressStyle);
    const [wakeLockEnabled, setWakeLockEnabled] = React.useState(getWakeLockPref);
    const [pullNextEnabled, setPullNextEnabled] = React.useState(getPullNextChapterPref);
    const wakeLockSupported = isWakeLockSupported();

    React.useEffect(() => {
        const sync = () => setProgressStyleState(getProgressStyle());
        window.addEventListener('bible_progress_style_changed', sync);
        return () => window.removeEventListener('bible_progress_style_changed', sync);
    }, []);

    React.useEffect(() => {
        const sync = () => setWakeLockEnabled(getWakeLockPref());
        window.addEventListener('bible_wake_lock_changed', sync);
        window.addEventListener('storage', sync);
        return () => {
            window.removeEventListener('bible_wake_lock_changed', sync);
            window.removeEventListener('storage', sync);
        };
    }, []);

    const handleProgressStyle = (style) => {
        setProgressStylePref(style);
        setProgressStyleState(style);
    };

    const handleWakeLockChange = (checked) => {
        setWakeLockPref(checked);
        setWakeLockEnabled(checked);
    };

    const handlePullNextChange = (checked) => {
        setPullNextChapterPref(checked);
        setPullNextEnabled(checked);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: '100%', sm: 340 } }
            }}
        >
            <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        阅读设置
                    </Typography>
                    <IconButton onClick={onClose} edge="end" size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider sx={{ mb: 2.5 }} />

                {/* 1. 阅读纸张背景主题 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                        📖 阅读主题
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.25 }}>
                        {PAPER_MODES.map((mode) => {
                            const isSelected = paperMode === mode.id;
                            return (
                                <ButtonBase
                                    key={mode.id}
                                    onClick={() => setPaperMode(mode.id)}
                                    sx={{
                                        p: 1.25,
                                        borderRadius: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        height: 64,
                                        bgcolor: mode.bgPaper,
                                        color: mode.textPrimary,
                                        border: '2px solid',
                                        borderColor: isSelected ? 'primary.main' : mode.divider,
                                        boxShadow: isSelected ? '0 0 0 1px currentColor' : 'none',
                                        transition: 'all 160ms ease',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                            {mode.nameZh}
                                        </Typography>
                                        {isSelected && (
                                            <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                        )}
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.65, fontSize: '0.72rem' }}
                                    >
                                        圣哉 · 恩典
                                    </Typography>
                                </ButtonBase>
                            );
                        })}
                    </Box>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* 2. 主题强调色 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                        🎨 强调色调
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center', justifyContent: 'center' }}>
                        {ACCENT_COLORS.map((accent) => {
                            const isSelected = accentColor === accent.id;
                            return (
                                <Tooltip key={accent.id} title={accent.nameZh} placement="top">
                                    <IconButton
                                        size="small"
                                        onClick={() => setAccentColor(accent.id)}
                                        sx={{
                                            p: 0.5,
                                            border: '2px solid',
                                            borderColor: isSelected ? accent.dotColor : 'transparent',
                                            transform: isSelected ? 'scale(1.2)' : 'scale(1)',
                                            transition: 'transform 150ms ease, border-color 150ms ease',
                                            '&:hover': { transform: 'scale(1.25)' },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 22,
                                                height: 22,
                                                borderRadius: '50%',
                                                bgcolor: accent.dotColor,
                                                boxShadow: isSelected ? `0 0 10px ${accent.dotColor}` : 'none',
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            );
                        })}
                    </Box>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* 3. 阅读区域宽度 */}
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            📐 阅读区域宽度
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main', bgcolor: 'action.hover', px: 1, py: 0.2, borderRadius: 1 }}>
                            {contentWidth}%
                        </Typography>
                    </Box>
                    <Box sx={{ px: 1, mb: 1 }}>
                        <Slider
                            value={contentWidth}
                            min={50}
                            max={100}
                            step={5}
                            onChange={(e, val) => setContentWidth(val)}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(val) => `${val}%`}
                            size="small"
                        />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'space-between' }}>
                        {[
                            { label: '窄 60%', value: 60 },
                            { label: '适中 75%', value: 75 },
                            { label: '80%', value: 80 },
                            { label: '宽 90%', value: 90 },
                            { label: '全宽', value: 100 },
                        ].map((opt) => (
                            <Button
                                key={opt.value}
                                size="small"
                                variant={contentWidth === opt.value ? 'contained' : 'outlined'}
                                onClick={() => setContentWidth(opt.value)}
                                sx={{
                                    minWidth: 0,
                                    px: 0.8,
                                    py: 0.2,
                                    fontSize: '0.72rem',
                                    borderRadius: 999,
                                }}
                            >
                                {opt.label}
                            </Button>
                        ))}
                    </Box>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* 4. 字体大小 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        🔤 字体大小
                    </Typography>
                    <FontSizeSlider />
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* 4b. 正文字体与行距 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        ✒️ 正文字体
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2 }}>
                        {READER_FONT_FAMILIES.map((font) => {
                            const selected = fontFamilyId === font.id;
                            return (
                                <ButtonBase
                                    key={font.id}
                                    onClick={() => setFontFamilyId(font.id)}
                                    sx={{
                                        p: 1,
                                        borderRadius: 1.5,
                                        border: '1.5px solid',
                                        borderColor: selected ? 'primary.main' : 'divider',
                                        bgcolor: selected ? 'action.selected' : 'transparent',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                    }}
                                >
                                    <Typography sx={{ fontSize: '0.8rem', fontWeight: selected ? 700 : 600, fontFamily: font.css }}>
                                        {font.nameZh}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontFamily: font.css, opacity: 0.7 }}>
                                        {font.previewZh}
                                    </Typography>
                                </ButtonBase>
                            );
                        })}
                    </Box>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        📏 行距
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {READER_LINE_HEIGHTS.map((item) => {
                            const selected = Math.abs(lineHeight - item.value) < 0.05;
                            return (
                                <Button
                                    key={item.id}
                                    size="small"
                                    variant={selected ? 'contained' : 'outlined'}
                                    onClick={() => setLineHeight(item.value)}
                                    sx={{ flex: 1, fontSize: '0.75rem', borderRadius: 1.5 }}
                                >
                                    {item.nameZh}
                                </Button>
                            );
                        })}
                    </Box>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* 5. 自定义文字颜色 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        🖌️ 自定义文字颜色
                    </Typography>
                    <ColorPicker />
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* 6. 章节阅读进度 */}
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                        📊 章节阅读进度
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.25 }}>
                        {[
                            { id: 'full', title: '灵动胶囊', desc: '呼吸淡化 · 实时节数' },
                            { id: 'minimal', title: '极简微标', desc: '角落仅悬浮小圆环' },
                            { id: 'top_only', title: '仅顶部进度条', desc: '3px 环境细线 · 零遮挡' },
                            { id: 'hidden', title: '完全隐藏', desc: '纯粹无干扰阅读' },
                        ].map((item) => {
                            const isSelected = progressStyle === item.id;
                            return (
                                <ButtonBase
                                    key={item.id}
                                    onClick={() => handleProgressStyle(item.id)}
                                    sx={{
                                        p: 1.25,
                                        borderRadius: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        height: 64,
                                        bgcolor: (t) =>
                                            t.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(0, 0, 0, 0.03)',
                                        border: '2px solid',
                                        borderColor: isSelected ? 'primary.main' : 'divider',
                                        boxShadow: isSelected ? '0 0 0 1px currentColor' : 'none',
                                        transition: 'all 160ms ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        },
                                    }}
                                >
                                    <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>
                                            {item.title}
                                        </Typography>
                                        {isSelected && (
                                            <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                        )}
                                    </Box>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.65, fontSize: '0.7rem' }}
                                    >
                                        {item.desc}
                                    </Typography>
                                </ButtonBase>
                            );
                        })}
                    </Box>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* 7. 保持屏幕常亮 */}
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                💡 保持屏幕常亮
                            </Typography>
                            {wakeLockSupported && wakeLockEnabled && (
                                <Chip
                                    label="常亮中"
                                    size="small"
                                    color="warning"
                                    sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600 }}
                                />
                            )}
                        </Box>
                        <Switch
                            checked={wakeLockEnabled}
                            onChange={(e) => handleWakeLockChange(e.target.checked)}
                            disabled={!wakeLockSupported}
                            color="warning"
                            size="small"
                        />
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.5 }}>
                        {wakeLockSupported
                            ? '读经期间防止设备自动熄屏或锁屏休眠；离开阅读页面后自动释放。'
                            : '当前设备或浏览器暂不支持屏幕常亮特性（建议使用最新版 Chrome、Edge 或 Safari）。'}
                    </Typography>
                </Box>

                <Divider sx={{ my: 2.5 }} />

                {/* 8. 章末上拉连读下一章 */}
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            ⏩ 章末上拉连读下一章
                        </Typography>
                        <Switch
                            checked={pullNextEnabled}
                            onChange={(e) => handlePullNextChange(e.target.checked)}
                            color="primary"
                            size="small"
                            inputProps={{ 'aria-label': '章末上拉连读下一章' }}
                        />
                    </Box>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.5 }}>
                        读到章节底部后继续上拉（或滚轮向下）即可顺滑进入下一章。
                    </Typography>
                </Box>
            </Box>
        </Drawer>
    );
};

export default SettingsPanel;
 